import { Fragment, useContext } from 'react';
import {
  IMultiEntityContext,
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext/ActivePosEntityContextV2';
import { SetActivePosEntityType } from 'src/contexts/ActivePosEntityContext/MultiEntityContextProvider';
import { Content } from 'src/contexts/ContentContext/Content';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useListingActions } from 'src/hooks/useListingActions';
import { Detail, DetailGroup } from 'src/modals/common';
import { ModalProps } from 'src/modals/Modal';
import { OpenLinkIcon } from 'src/svgs';
import { ContentId } from 'src/utils/constants/contentId';
import { selectDistinctListingIds } from 'src/utils/ticketUtils';
import {
  ActionOutboxEntityType,
  ListingDetailDataField,
  Ticket,
} from 'src/WebApiController';

import * as styles from '../SaleDetails.css';

const renderListing = (
  contentId: string,
  setModal: (modal: ModalProps) => void,
  setActivePosEntity: SetActivePosEntityType,
  listingId?: number | null,
  showTitle?: boolean,
  openListingModal?: (
    listingId: number,
    dataFields: ListingDetailDataField[],
    forceRefresh?: boolean,
    resetData?: boolean,
    sectional?: boolean,
    mouseEvent?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void
) => {
  if (!listingId) return null;

  return (
    <DetailGroup>
      <Detail
        label={showTitle && <Content id={contentId} />}
        detail={
          <Button
            variant="link"
            onClick={(e) => {
              if (openListingModal) {
                openListingModal(
                  listingId,
                  [ListingDetailDataField.Basic],
                  false,
                  false,
                  false,
                  e
                );
              }
            }}
            style={{ padding: '0' }}
          >
            <Stack style={{ alignItems: 'center' }}>
              <div className={styles.ticketListingId}>{'#' + listingId}</div>
              <OpenLinkIcon
                size={vars.iconSize.s}
                fill={vars.color.textBrand}
                withHoverEffect
                style={{ marginBottom: 2 }}
              />
            </Stack>
          </Button>
        }
      />
    </DetailGroup>
  );
};

export const ListingLinksDetail = ({
  listingId,
  originatedFromListingId,
  tickets,
  showTitle = true,
}: {
  listingId?: number | null;
  originatedFromListingId?: number | null;
  tickets?: Ticket[];
  showTitle?: boolean;
}) => {
  const { setModal } = useContext(ModalContext);
  const { openListingModal } = useListingActions();
  const { setActivePosEntity } =
    useActivePosEntityContext() as IMultiEntityContext<IPosEntity>;

  const distinctListingIds = tickets?.length
    ? selectDistinctListingIds(tickets)
    : [];

  const hasOriginatedListing =
    originatedFromListingId &&
    distinctListingIds.includes(originatedFromListingId);

  return (
    <>
      {tickets?.length ? (
        <>
          {!hasOriginatedListing &&
            renderListing(
              ContentId.OriginatedListing,
              setModal,
              setActivePosEntity,
              originatedFromListingId,
              showTitle,
              openListingModal
            )}
          {distinctListingIds.map((listingId, index) => (
            <Fragment key={`${listingId}-${index}`}>
              {renderListing(
                listingId === originatedFromListingId
                  ? ContentId.OriginatedListing
                  : ContentId.AllocatedListing,
                setModal,
                setActivePosEntity,
                listingId,
                showTitle,
                openListingModal
              )}
            </Fragment>
          ))}
        </>
      ) : (
        <>
          {originatedFromListingId &&
            renderListing(
              ContentId.OriginatedListing,
              setModal,
              setActivePosEntity,
              originatedFromListingId,
              showTitle,
              openListingModal
            )}
          {listingId !== originatedFromListingId &&
            renderListing(
              ContentId.AllocatedListing,
              setModal,
              setActivePosEntity,
              listingId,
              showTitle,
              openListingModal
            )}
        </>
      )}
    </>
  );
};
